import React, { useEffect, useState } from "react";
import {
    Handshake,
    TrendingUp,
    Package,
    Truck,
    GraduationCap,
    Megaphone,
    ShieldCheck,
    MapPin,
    Check,
    Send,
    Loader2,
    ArrowRight,
    Sparkles,
    Shirt,
    ShoppingBag,
    Coffee,
    Gift,
    Palette,
    Store,
    Star,
    ChevronRight,
    Mail,
    Phone,
    MessageCircle,
} from "lucide-react";
import axios from "axios";
import { toast } from "sonner";
import { API } from "../lib/api";
import { Header } from "../components/site/Header";
import { Footer } from "../components/site/Footer";
import { WhatsAppButton } from "../components/site/WhatsAppButton";
import { useSiteContent, getSection } from "../context/SiteContentContext";

const ICON_MAP = {
    TrendingUp,
    MapPin,
    GraduationCap,
    Megaphone,
    Package,
    ShieldCheck,
    Truck,
    Handshake,
    Shirt,
    ShoppingBag,
    Coffee,
    Store,
    Gift,
    Palette,
    Star,
    Sparkles,
};

const STYLE_CYCLE = [
    { color: "text-brand-green", bg: "bg-brand-green-soft" },
    { color: "text-brand-orange", bg: "bg-brand-orange-soft" },
    { color: "text-[#CFA059]", bg: "bg-brand-pink" },
    { color: "text-[#8691A0]", bg: "bg-brand-yellow" },
    { color: "text-brand-green", bg: "bg-brand-green-soft" },
    { color: "text-brand-ink", bg: "bg-brand-cream" },
];

const FALLBACK = {
    hero_badge: "Proposta comercial",
    hero_title_pre: "Seja",
    hero_title_highlight: "parceiro",
    hero_title_suffix: "Sabor Líquido.",
    hero_description:
        "Representamos uma gama premium de sumos naturais, detox, protein e tea, prontos a servir. Cria um novo centro de receita no teu negócio — com marca, margem e suporte.",
    hero_image:
        "https://images.unsplash.com/photo-1686771416282-3888ddaf249b?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    merch_image:
        "https://images.unsplash.com/photo-1728034261599-53487ee4281c?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    counter_image:
        "https://images.unsplash.com/photo-1563245374-27386492c152?crop=entropy&cs=srgb&fm=jpg&q=85&w=1400",
    stat_1_n: "30-40%",
    stat_1_l: "Margem",
    stat_2_n: "48-72h",
    stat_2_l: "Entrega",
    stat_3_n: "1:1",
    stat_3_l: "Gestor dedicado",
    contact_email: "parceiros@saborliquido.pt",
    contact_phone: "+351 912 345 678",
    contact_whatsapp: "351912345678",
    pricing_tiers: [
        { tier: "Arranque", range: "até 50 uni / mês", margin: "30%", unit: "3,15 € / uni", note: "PVP recomendado 4,50 €" },
        { tier: "Crescimento", range: "50 – 150 uni / mês", margin: "35%", unit: "2,93 € / uni", note: "Envio incluído em Portugal continental" },
        { tier: "Volume", range: "150 – 400 uni / mês", margin: "38%", unit: "2,79 € / uni", note: "Expositor de balcão oferecido" },
        { tier: "Distribuidor", range: "400+ uni / mês", margin: "40%", unit: "2,70 € / uni", note: "Condições dedicadas + exclusividade de zona" },
    ],
    benefits: [
        { icon_key: "TrendingUp", title: "Margem atrativa", text: "Entre 30% e 40% de margem sobre o PVP, com escalões por volume." },
        { icon_key: "MapPin", title: "Exclusividade por zona", text: "Territórios atribuídos com base em volume mínimo mensal — sem canibalização." },
        { icon_key: "GraduationCap", title: "Formação de produto", text: "Formação inicial presencial ou online + manual técnico e de vendas." },
        { icon_key: "Megaphone", title: "Material de comunicação", text: "Kit de apresentação, fotos de produto, vídeos curtos e templates para redes sociais." },
        { icon_key: "Package", title: "Expositor de balcão", text: "Oferecemos um display de marca para as primeiras encomendas acima do valor X." },
        { icon_key: "ShieldCheck", title: "Marca protegida", text: "Todas as embalagens chegam já com a identidade Sabor Líquido — sem trabalho extra." },
    ],
    proposal: [
        { label: "Margem ao parceiro", value: "30% – 40%", sub: "sobre PVP recomendado" },
        { label: "Encomenda mínima inicial", value: "200 €", sub: "≈ 45 unidades (mistura livre)" },
        { label: "Reforço mínimo", value: "100 €", sub: "com envio em 48–72h" },
        { label: "Condições de pagamento", value: "Pronto ou 30d", sub: "30 dias para clientes recorrentes" },
        { label: "Exclusividade territorial", value: "Sim", sub: "mediante volume mínimo mensal" },
        { label: "Kit de arranque", value: "Incluído", sub: "expositor + material + degustação" },
    ],
    steps: [
        { n: 1, title: "Pede proposta", text: "Preenche o formulário ou fala connosco pelo WhatsApp." },
        { n: 2, title: "Análise e pitch", text: "Marcamos uma call de 20 min para perceber o teu negócio e zona." },
        { n: 3, title: "Envio de kit", text: "Enviamos kit degustação + catálogo digital para experimentares." },
        { n: 4, title: "Contrato & primeira encomenda", text: "Assinamos condições comerciais e preparamos a tua estreia." },
        { n: 5, title: "Onboarding & venda", text: "Formação + lançamento da operação com apoio contínuo do gestor dedicado." },
    ],
    merch_items: [
        { icon_key: "Shirt", name: "T-shirts de marca", tag: "Tecido orgânico" },
        { icon_key: "ShoppingBag", name: "Totes & sacos", tag: "Pano reciclado" },
        { icon_key: "Coffee", name: "Shakers & copos", tag: "Reutilizáveis" },
        { icon_key: "Store", name: "Expositor de balcão", tag: "Madeira clara" },
        { icon_key: "Gift", name: "Caixa degustação", tag: "Presente ideal" },
        { icon_key: "Palette", name: "Material gráfico", tag: "Menus, flyers, cartazes" },
    ],
    faqs: [
        { q: "Quem pode ser parceiro?", a: "Cafés, pastelarias, ginásios, SPAs, hotéis, clínicas, lojas de conveniência premium, food trucks, quiosques e microempresários com canal de venda próprio." },
        { q: "Preciso de investimento inicial elevado?", a: "Não. Começas com 200 € de encomenda inicial — cerca de 45 unidades — e vais reforçando conforme vendes. O kit de comunicação inicial está incluído." },
        { q: "Posso escolher livremente os sabores?", a: "Sim, a composição da encomenda é livre dentro do catálogo Fruit, Detox, Protein e Tea, com variações por sabor e embalagem." },
        { q: "Como funciona a exclusividade?", a: "Atribuímos zonas geográficas (concelho ou freguesia urbana) mediante cumprimento de volume mínimo mensal acordado em contrato." },
        { q: "Como é feita a entrega?", a: "Envio em frio, em 48–72h úteis para Portugal continental. Ilhas e UE sob cotação." },
    ],
};

export default function Parceiros() {
    const { content } = useSiteContent();
    const cms = getSection(content, "partners", {});
    const c = { ...FALLBACK, ...cms };
    const [wantsSamples, setWantsSamples] = useState(false);

    useEffect(() => {
        const prev = document.title;
        document.title = "Parceiros B2B — Sabor Líquido";
        return () => {
            document.title = prev;
        };
    }, []);

    return (
        <div className="bg-brand-cream" data-testid="partners-page">
            <Header />

            {/* HERO */}
            <section className="relative overflow-hidden pt-10 md:pt-16 pb-16 md:pb-24">
                <div
                    aria-hidden
                    className="absolute -top-40 -right-40 h-[600px] w-[600px] rounded-full bg-brand-orange/20 blur-3xl"
                />
                <div
                    aria-hidden
                    className="absolute -bottom-40 -left-40 h-[600px] w-[600px] rounded-full bg-brand-green/15 blur-3xl"
                />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                        <div>
                            <span className="inline-flex items-center gap-2 rounded-full bg-brand-green-soft text-brand-green px-3 py-1.5 text-xs font-bold uppercase tracking-widest">
                                <Handshake className="h-3.5 w-3.5" />
                                {c.hero_badge}
                            </span>
                            <h1 className="mt-4 font-display font-medium text-4xl md:text-5xl lg:text-6xl text-brand-ink leading-[1.05] tracking-tight">
                                {c.hero_title_pre}{" "}
                                <span className="text-brand-orange">{c.hero_title_highlight}</span>{" "}
                                {c.hero_title_suffix}
                            </h1>
                            <p className="mt-5 text-lg md:text-xl text-brand-ink-soft leading-relaxed max-w-xl">
                                {c.hero_description}
                            </p>
                            <div className="mt-7 flex flex-wrap items-center gap-3">
                                <a
                                    href="#pedir-proposta"
                                    data-testid="hero-cta-proposal"
                                    className="inline-flex items-center gap-2 rounded-full bg-brand-green hover:bg-brand-green-dark text-white px-6 py-3.5 text-sm font-semibold transition-colors shadow-pop"
                                >
                                    <Send className="h-4 w-4" />
                                    Pedir proposta comercial
                                </a>
                                <a
                                    href="#proposta"
                                    data-testid="hero-cta-learn"
                                    className="inline-flex items-center gap-1.5 rounded-full bg-white hover:bg-brand-cream text-brand-ink px-6 py-3.5 text-sm font-semibold transition-colors border-2 border-brand-green/15"
                                >
                                    Ver condições <ChevronRight className="h-4 w-4" />
                                </a>
                            </div>
                            <div className="mt-8 grid grid-cols-3 gap-4 max-w-md">
                                <MiniStat n={c.stat_1_n} l={c.stat_1_l} />
                                <MiniStat n={c.stat_2_n} l={c.stat_2_l} />
                                <MiniStat n={c.stat_3_n} l={c.stat_3_l} />
                            </div>
                        </div>

                        <div className="relative">
                            <div className="absolute -top-6 -left-6 h-24 w-24 rounded-full bg-brand-orange/25 blur-2xl" />
                            <div className="relative rounded-2xl overflow-hidden shadow-pop ring-1 ring-brand-green/10">
                                <img
                                    src={c.hero_image}
                                    alt="Parceria de negócios Sabor Líquido"
                                    className="w-full h-[400px] md:h-[500px] object-cover"
                                />
                                <div className="absolute inset-x-0 bottom-0 p-6 bg-gradient-to-t from-black/60 to-transparent">
                                    <div className="inline-flex items-center gap-1.5 rounded-full bg-white/95 backdrop-blur px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest text-brand-ink">
                                        <Sparkles className="h-3 w-3 text-brand-orange" />
                                        Novo em Portugal
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* BENEFITS */}
            <section className="relative py-16 md:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl">
                        <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-orange">
                            <Star className="h-3.5 w-3.5" /> Vantagens
                        </span>
                        <h2 className="mt-3 font-display font-medium text-3xl md:text-4xl lg:text-5xl text-brand-ink leading-tight tracking-tight">
                            Porquê representar a{" "}
                            <span className="text-brand-green">Sabor Líquido</span>?
                        </h2>
                    </div>
                    <div className="mt-10 grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                        {(c.benefits || []).map((b, i) => {
                            const Icon = ICON_MAP[b.icon_key] || Star;
                            const s = STYLE_CYCLE[i % STYLE_CYCLE.length];
                            return (
                                <article
                                    key={i}
                                    data-testid={`benefit-${i}`}
                                    className="group rounded-2xl bg-brand-cream/60 hover:bg-white hover:shadow-pop ring-1 ring-brand-green/10 p-6 transition-all"
                                >
                                    <div
                                        className={`h-12 w-12 rounded-2xl ${s.bg} flex items-center justify-center`}
                                    >
                                        <Icon className={`h-6 w-6 ${s.color}`} />
                                    </div>
                                    <h3 className="mt-4 font-display font-medium text-xl text-brand-ink">
                                        {b.title}
                                    </h3>
                                    <p className="mt-2 text-sm text-brand-ink-soft leading-relaxed">
                                        {b.text}
                                    </p>
                                </article>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* PROPOSAL TABLE */}
            <section id="proposta" className="py-16 md:py-24 bg-brand-cream">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-10 items-center">
                        <div>
                            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-green">
                                <Handshake className="h-3.5 w-3.5" /> Condições comerciais
                            </span>
                            <h2 className="mt-3 font-display font-medium text-3xl md:text-4xl lg:text-5xl text-brand-ink leading-tight tracking-tight">
                                Transparente.{" "}
                                <span className="text-brand-orange">Simples.</span> Justo.
                            </h2>
                            <p className="mt-5 text-lg text-brand-ink-soft leading-relaxed">
                                Valores de referência para parceiros nacionais. Cada proposta é
                                depois ajustada ao teu volume, zona e formato de negócio.
                            </p>
                            <a
                                href="#pedir-proposta"
                                className="mt-6 inline-flex items-center gap-1.5 rounded-full bg-brand-ink hover:bg-brand-green text-white px-5 py-3 text-sm font-semibold transition-colors"
                                data-testid="proposal-cta"
                            >
                                Falar com um gestor <ArrowRight className="h-4 w-4" />
                            </a>
                        </div>
                        <div className="grid sm:grid-cols-2 gap-4">
                            {(c.proposal || []).map((p, i) => (
                                <div
                                    key={i}
                                    data-testid={`proposal-${i}`}
                                    className="rounded-2xl bg-white p-5 shadow-soft ring-1 ring-brand-green/10"
                                >
                                    <div className="text-[11px] uppercase tracking-widest font-bold text-brand-ink-soft">
                                        {p.label}
                                    </div>
                                    <div className="mt-2 font-display font-medium text-2xl text-brand-ink">
                                        {p.value}
                                    </div>
                                    <div className="mt-1 text-xs text-brand-ink-soft">{p.sub}</div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* VOLUME PRICING TABLE */}
            <section id="precos-volume" className="py-16 md:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl">
                        <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-orange">
                            <TrendingUp className="h-3.5 w-3.5" /> Preços por volume
                        </span>
                        <h2 className="mt-3 font-display font-medium text-3xl md:text-4xl lg:text-5xl text-brand-ink leading-tight tracking-tight">
                            Quanto mais vendes,{" "}
                            <span className="italic text-brand-orange">melhor a margem.</span>
                        </h2>
                        <p className="mt-4 text-lg text-brand-ink-soft">
                            Escalões de referência por unidade de polpa (dose individual).
                            Valores sem IVA, ajustáveis em proposta final.
                        </p>
                    </div>
                    <div
                        className="mt-10 overflow-x-auto rounded-2xl ring-1 ring-brand-ink/10"
                        data-testid="pricing-table"
                    >
                        <table className="w-full min-w-[640px] text-left">
                            <thead>
                                <tr className="border-b border-brand-ink/10 bg-brand-cream/60">
                                    <th className="px-6 py-4 text-[11px] uppercase tracking-[0.2em] font-bold text-brand-ink-soft">
                                        Escalão
                                    </th>
                                    <th className="px-6 py-4 text-[11px] uppercase tracking-[0.2em] font-bold text-brand-ink-soft">
                                        Volume mensal
                                    </th>
                                    <th className="px-6 py-4 text-[11px] uppercase tracking-[0.2em] font-bold text-brand-ink-soft">
                                        Margem
                                    </th>
                                    <th className="px-6 py-4 text-[11px] uppercase tracking-[0.2em] font-bold text-brand-ink-soft">
                                        Preço unitário
                                    </th>
                                    <th className="px-6 py-4 text-[11px] uppercase tracking-[0.2em] font-bold text-brand-ink-soft">
                                        Inclui
                                    </th>
                                </tr>
                            </thead>
                            <tbody>
                                {(c.pricing_tiers || []).map((t, i) => (
                                    <tr
                                        key={i}
                                        data-testid={`pricing-tier-${i}`}
                                        className="border-b border-brand-ink/5 last:border-0 hover:bg-brand-cream/40 transition-colors"
                                    >
                                        <td className="px-6 py-5 font-display font-medium text-xl text-brand-ink">
                                            {t.tier}
                                        </td>
                                        <td className="px-6 py-5 text-sm text-brand-ink-soft">
                                            {t.range}
                                        </td>
                                        <td className="px-6 py-5">
                                            <span className="inline-flex rounded-full bg-brand-green-soft text-brand-green px-3 py-1 text-sm font-bold">
                                                {t.margin}
                                            </span>
                                        </td>
                                        <td className="px-6 py-5 font-display font-medium text-xl text-brand-ink">
                                            {t.unit}
                                        </td>
                                        <td className="px-6 py-5 text-xs text-brand-ink-soft">
                                            {t.note}
                                        </td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                    <p className="mt-4 text-xs text-brand-ink-soft">
                        * Margens calculadas sobre o PVP recomendado. Kits e bolsas a granel
                        têm tabela própria — pede a proposta completa.
                    </p>
                </div>
            </section>

            {/* FREE SAMPLES BANNER */}
            <section className="py-16 md:py-20 bg-brand-cream">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div
                        className="relative overflow-hidden rounded-2xl bg-brand-ink text-white px-8 py-12 md:px-14 md:py-16"
                        data-testid="samples-banner"
                    >
                        <div
                            aria-hidden
                            className="absolute inset-0 opacity-[0.06] bg-hex-pattern pointer-events-none"
                            style={{ backgroundImage: "url('/brand/pattern-1.png')" }}
                        />
                        <div className="relative grid lg:grid-cols-3 gap-8 items-center">
                            <div className="lg:col-span-2">
                                <span className="inline-flex items-center gap-2 rounded-full bg-white/10 px-3 py-1.5 text-[11px] font-bold uppercase tracking-[0.2em] text-brand-yellow">
                                    <Gift className="h-3.5 w-3.5" /> Sem compromisso
                                </span>
                                <h2 className="mt-4 font-display font-medium text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight">
                                    Prova antes de decidir.{" "}
                                    <span className="italic text-brand-orange">
                                        Kit de amostras grátis.
                                    </span>
                                </h2>
                                <p className="mt-4 text-white/75 leading-relaxed max-w-2xl">
                                    Enviamos 6 doses de polpa (Fruit, Detox, Protein e Tea) em
                                    frio, sem custos, para negócios com espaço físico em Portugal
                                    continental. Só pagas se decidires avançar.
                                </p>
                            </div>
                            <div className="lg:text-right">
                                <a
                                    href="#pedir-proposta"
                                    onClick={() => setWantsSamples(true)}
                                    data-testid="samples-cta"
                                    className="inline-flex items-center gap-2 rounded-full bg-white text-brand-ink hover:bg-brand-cream px-7 py-4 text-sm font-semibold transition-all shadow-pop hover:translate-y-[-2px]"
                                >
                                    <Gift className="h-4 w-4" />
                                    Pedir amostras grátis
                                </a>
                                <p className="mt-3 text-[11px] text-white/60 lg:text-right">
                                    Resposta em 24h úteis · envio em 48–72h
                                </p>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* JOURNEY STEPS */}
            <section className="py-16 md:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="max-w-3xl">
                        <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-orange">
                            <Truck className="h-3.5 w-3.5" /> Como começar
                        </span>
                        <h2 className="mt-3 font-display font-medium text-3xl md:text-4xl lg:text-5xl text-brand-ink leading-tight tracking-tight">
                            De <span className="text-brand-green">pedido a venda</span> em{" "}
                            {c.steps?.length || 5} passos.
                        </h2>
                    </div>
                    <ol
                        className="mt-10 grid gap-4"
                        style={{
                            gridTemplateColumns: `repeat(${Math.min(
                                Math.max(c.steps?.length || 5, 1),
                                5,
                            )}, minmax(0, 1fr))`,
                        }}
                    >
                        {(c.steps || []).map((s, i) => (
                            <li
                                key={i}
                                data-testid={`step-${s.n}`}
                                className="relative rounded-2xl bg-brand-cream/60 ring-1 ring-brand-green/10 p-5"
                            >
                                <div className="h-10 w-10 rounded-full bg-brand-green text-white font-display font-medium text-lg flex items-center justify-center shadow-pop">
                                    {s.n}
                                </div>
                                <div className="mt-3 font-display font-medium text-lg text-brand-ink">
                                    {s.title}
                                </div>
                                <p className="mt-1 text-xs text-brand-ink-soft leading-relaxed">
                                    {s.text}
                                </p>
                            </li>
                        ))}
                    </ol>
                </div>
            </section>

            {/* MERCHANDISING */}
            <section
                id="merch"
                className="py-16 md:py-24 bg-brand-ink text-white relative overflow-hidden"
            >
                <div
                    aria-hidden
                    className="absolute inset-0 opacity-[0.06] bg-hex-pattern pointer-events-none"
                    style={{ backgroundImage: "url('/brand/pattern-3.png')" }}
                />
                <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                    <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
                        <div>
                            <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-orange">
                                <Gift className="h-3.5 w-3.5" /> Merchandising
                            </span>
                            <h2 className="mt-3 font-display font-medium text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight">
                                A marca, também{" "}
                                <span className="text-brand-orange">fora do copo.</span>
                            </h2>
                            <p className="mt-5 text-lg text-white/70 leading-relaxed max-w-xl">
                                Todo o merchandising Sabor Líquido que podes exibir no teu espaço
                                ou oferecer aos teus clientes. Mostra que fazes parte de uma marca
                                com alma e apresentação cuidada.
                            </p>
                            <div className="mt-4 inline-flex items-center gap-1.5 rounded-full bg-white/10 backdrop-blur px-3 py-1.5 text-[11px] font-bold uppercase tracking-widest text-white/80">
                                <Sparkles className="h-3 w-3 text-brand-orange" />
                                Oferta especial para novos parceiros
                            </div>
                        </div>
                        <div className="relative">
                            <div className="relative rounded-2xl overflow-hidden shadow-pop ring-1 ring-white/10">
                                <img
                                    src={c.merch_image}
                                    alt="Merchandising Sabor Líquido"
                                    className="w-full h-[380px] md:h-[440px] object-cover"
                                />
                            </div>
                        </div>
                    </div>

                    <div className="mt-14 grid sm:grid-cols-2 md:grid-cols-3 gap-4">
                        {(c.merch_items || []).map((m, i) => {
                            const Icon = ICON_MAP[m.icon_key] || Gift;
                            return (
                                <div
                                    key={i}
                                    data-testid={`merch-${i}`}
                                    className="group rounded-2xl bg-white/5 hover:bg-white/10 ring-1 ring-white/10 p-5 transition-all"
                                >
                                    <div className="h-12 w-12 rounded-2xl bg-brand-orange/20 flex items-center justify-center">
                                        <Icon className="h-6 w-6 text-brand-orange" />
                                    </div>
                                    <div className="mt-4 font-display font-medium text-lg">
                                        {m.name}
                                    </div>
                                    <div className="mt-1 text-xs text-white/60">{m.tag}</div>
                                </div>
                            );
                        })}
                    </div>
                </div>
            </section>

            {/* POS / COUNTER */}
            <section className="py-16 md:py-24 bg-white">
                <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-10 items-center">
                    <div className="order-2 lg:order-1 relative rounded-2xl overflow-hidden shadow-pop ring-1 ring-brand-green/10">
                        <img
                            src={c.counter_image}
                            alt="Ponto de venda"
                            className="w-full h-[380px] md:h-[440px] object-cover"
                        />
                    </div>
                    <div className="order-1 lg:order-2">
                        <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-green">
                            <Store className="h-3.5 w-3.5" /> Ponto de venda
                        </span>
                        <h2 className="mt-3 font-display font-medium text-3xl md:text-4xl lg:text-5xl text-brand-ink leading-tight tracking-tight">
                            O teu balcão,{" "}
                            <span className="text-brand-orange">preparado para vender.</span>
                        </h2>
                        <ul className="mt-6 space-y-3">
                            {[
                                "Expositor de balcão em cortiça e madeira clara",
                                "Menu de mesa e tabela de preços imprimíveis",
                                "Sinalética para frigorífico e montra",
                                "QR codes para encomenda rápida por parte do cliente final",
                                "Fotos de produto em alta resolução para redes sociais",
                            ].map((f) => (
                                <li key={f} className="flex items-start gap-2">
                                    <span className="mt-0.5 shrink-0 h-5 w-5 rounded-full bg-brand-green-soft flex items-center justify-center">
                                        <Check className="h-3 w-3 text-brand-green" />
                                    </span>
                                    <span className="text-brand-ink-soft">{f}</span>
                                </li>
                            ))}
                        </ul>
                    </div>
                </div>
            </section>

            {/* FAQ */}
            <section className="py-16 md:py-24 bg-brand-cream">
                <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
                    <span className="inline-flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-brand-orange">
                        <Sparkles className="h-3.5 w-3.5" /> Perguntas frequentes
                    </span>
                    <h2 className="mt-3 font-display font-medium text-3xl md:text-4xl lg:text-5xl text-brand-ink leading-tight tracking-tight">
                        Tudo o que normalmente{" "}
                        <span className="text-brand-green">nos perguntam.</span>
                    </h2>
                    <div className="mt-10 space-y-3">
                        {(c.faqs || []).map((f, i) => (
                            <details
                                key={i}
                                data-testid={`faq-${i}`}
                                className="group rounded-2xl bg-white ring-1 ring-brand-green/10 p-5 shadow-soft open:shadow-pop"
                            >
                                <summary className="flex items-center justify-between cursor-pointer font-display font-medium text-lg text-brand-ink">
                                    {f.q}
                                    <ChevronRight className="h-4 w-4 text-brand-green transition-transform group-open:rotate-90" />
                                </summary>
                                <p className="mt-3 text-sm text-brand-ink-soft leading-relaxed">
                                    {f.a}
                                </p>
                            </details>
                        ))}
                    </div>
                </div>
            </section>

            {/* REQUEST PROPOSAL FORM */}
            <PartnerForm
                contact={c}
                wantsSamples={wantsSamples}
                setWantsSamples={setWantsSamples}
            />

            <Footer />
            <WhatsAppButton />
        </div>
    );
}

const MiniStat = ({ n, l }) => (
    <div className="rounded-2xl bg-white/70 backdrop-blur px-3 py-3 text-center shadow-soft">
        <div className="font-display font-medium text-xl md:text-2xl text-brand-ink">{n}</div>
        <div className="text-[10px] uppercase tracking-widest font-bold text-brand-ink-soft mt-0.5">
            {l}
        </div>
    </div>
);

// ============================================================
// PARTNER FORM
// ============================================================
function PartnerForm({ contact, wantsSamples, setWantsSamples }) {
    const [form, setForm] = useState({
        name: "",
        email: "",
        phone: "",
        city: "",
        business_type: "",
        message: "",
    });
    const [sending, setSending] = useState(false);
    const [ok, setOk] = useState(false);

    const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

    const submit = async (e) => {
        e.preventDefault();
        if (!form.name || !form.email) {
            toast.error("Preenche pelo menos nome e email");
            return;
        }
        setSending(true);
        try {
            await axios.post(`${API}/leads`, {
                name: form.name.trim(),
                email: form.email.trim().toLowerCase(),
                phone: form.phone || null,
                city: form.city || null,
                business_type: form.business_type || null,
                message: wantsSamples
                    ? `[PEDIDO DE KIT DE AMOSTRAS GRÁTIS] ${form.message || ""}`.trim()
                    : form.message || null,
                interest: "parceiro",
                line_interest: null,
            });
            setOk(true);
            setWantsSamples(false);
            setForm({
                name: "",
                email: "",
                phone: "",
                city: "",
                business_type: "",
                message: "",
            });
            toast.success("Pedido enviado! Voltamos a ti em 24h.");
        } catch (err) {
            toast.error(err.response?.data?.detail || "Erro ao enviar. Tenta novamente.");
        } finally {
            setSending(false);
        }
    };

    return (
        <section
            id="pedir-proposta"
            className="py-16 md:py-24 bg-brand-green text-white relative overflow-hidden"
        >
            <div
                aria-hidden
                className="absolute -top-40 -left-40 h-[500px] w-[500px] rounded-full bg-brand-orange/30 blur-3xl"
            />
            <div
                aria-hidden
                className="absolute -bottom-40 -right-40 h-[500px] w-[500px] rounded-full bg-white/10 blur-3xl"
            />
            <div className="relative max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-5 gap-10">
                <div className="lg:col-span-2">
                    <span className="inline-flex items-center gap-2 rounded-full bg-white/15 px-3 py-1.5 text-xs font-bold uppercase tracking-widest">
                        <Send className="h-3.5 w-3.5" />
                        Pedir proposta
                    </span>
                    <h2 className="mt-4 font-display font-medium text-3xl md:text-4xl lg:text-5xl leading-tight tracking-tight">
                        Vamos <span className="text-brand-yellow">falar de números.</span>
                    </h2>
                    <p className="mt-4 text-white/80 leading-relaxed">
                        Preenche o formulário e recebes, em 24h, proposta personalizada com
                        margens, condições de pagamento e disponibilidade na tua zona.
                    </p>
                    <div className="mt-8 space-y-3 text-sm">
                        {contact.contact_email && (
                            <a
                                href={`mailto:${contact.contact_email}`}
                                className="flex items-center gap-2 text-white/90 hover:text-white"
                                data-testid="partner-email"
                            >
                                <Mail className="h-4 w-4" /> {contact.contact_email}
                            </a>
                        )}
                        {contact.contact_phone && (
                            <a
                                href={`tel:${contact.contact_phone.replace(/\s/g, "")}`}
                                className="flex items-center gap-2 text-white/90 hover:text-white"
                                data-testid="partner-phone"
                            >
                                <Phone className="h-4 w-4" /> {contact.contact_phone}
                            </a>
                        )}
                        {contact.contact_whatsapp && (
                            <a
                                href={`https://wa.me/${contact.contact_whatsapp}`}
                                target="_blank"
                                rel="noreferrer"
                                className="flex items-center gap-2 text-white/90 hover:text-white"
                                data-testid="partner-whatsapp"
                            >
                                <MessageCircle className="h-4 w-4" /> WhatsApp
                            </a>
                        )}
                    </div>
                </div>

                <form
                    onSubmit={submit}
                    data-testid="partner-form"
                    className="lg:col-span-3 rounded-2xl bg-white text-brand-ink p-6 md:p-8 shadow-pop"
                >
                    {ok ? (
                        <div className="text-center py-12" data-testid="partner-form-success">
                            <div className="h-16 w-16 rounded-full bg-brand-green-soft mx-auto flex items-center justify-center">
                                <Check className="h-8 w-8 text-brand-green" />
                            </div>
                            <h3 className="mt-6 font-display font-medium text-2xl text-brand-ink">
                                Pedido recebido!
                            </h3>
                            <p className="mt-2 text-sm text-brand-ink-soft">
                                A equipa comercial vai contactar-te em 24h úteis.
                            </p>
                            <button
                                type="button"
                                onClick={() => setOk(false)}
                                className="mt-6 text-sm font-bold text-brand-green hover:underline"
                            >
                                Enviar novo pedido
                            </button>
                        </div>
                    ) : (
                        <>
                            <div className="grid sm:grid-cols-2 gap-4">
                                <Field label="Nome*" testid="partner-field-name">
                                    <input
                                        required
                                        value={form.name}
                                        onChange={set("name")}
                                        data-testid="partner-input-name"
                                        placeholder="O teu nome"
                                        className={inputCls}
                                    />
                                </Field>
                                <Field label="Email*" testid="partner-field-email">
                                    <input
                                        required
                                        type="email"
                                        value={form.email}
                                        onChange={set("email")}
                                        data-testid="partner-input-email"
                                        placeholder="o.teu@email.com"
                                        className={inputCls}
                                    />
                                </Field>
                                <Field label="Telefone" testid="partner-field-phone">
                                    <input
                                        type="tel"
                                        value={form.phone}
                                        onChange={set("phone")}
                                        data-testid="partner-input-phone"
                                        placeholder="+351..."
                                        className={inputCls}
                                    />
                                </Field>
                                <Field label="Cidade / Concelho" testid="partner-field-city">
                                    <input
                                        value={form.city}
                                        onChange={set("city")}
                                        data-testid="partner-input-city"
                                        placeholder="Ex: Porto"
                                        className={inputCls}
                                    />
                                </Field>
                                <div className="sm:col-span-2">
                                    <Field label="Tipo de negócio" testid="partner-field-business">
                                        <select
                                            value={form.business_type}
                                            onChange={set("business_type")}
                                            data-testid="partner-input-business"
                                            className={inputCls}
                                        >
                                            <option value="">Seleciona...</option>
                                            <option>Café / Pastelaria</option>
                                            <option>Ginásio / Box</option>
                                            <option>Hotel / Alojamento</option>
                                            <option>SPA / Clínica</option>
                                            <option>Restaurante</option>
                                            <option>Food truck / Quiosque</option>
                                            <option>Loja de conveniência</option>
                                            <option>Outro</option>
                                        </select>
                                    </Field>
                                </div>
                                <div className="sm:col-span-2">
                                    <Field label="Mensagem" testid="partner-field-message">
                                        <textarea
                                            rows={4}
                                            value={form.message}
                                            onChange={set("message")}
                                            data-testid="partner-input-message"
                                            placeholder="Conta-nos sobre o teu negócio, zona, volume estimado..."
                                            className={`${inputCls} resize-none`}
                                        />
                                    </Field>
                                </div>
                            </div>
                            <label
                                className="mt-5 flex items-start gap-3 rounded-2xl bg-brand-cream/70 ring-1 ring-brand-ink/10 p-4 cursor-pointer select-none"
                                data-testid="partner-samples-label"
                            >
                                <input
                                    type="checkbox"
                                    checked={wantsSamples}
                                    onChange={(e) => setWantsSamples(e.target.checked)}
                                    data-testid="partner-samples-checkbox"
                                    className="mt-0.5 h-4 w-4 accent-[#1C221F]"
                                />
                                <span>
                                    <span className="block text-sm font-semibold text-brand-ink">
                                        Quero receber o kit de amostras grátis
                                    </span>
                                    <span className="block text-xs text-brand-ink-soft mt-0.5">
                                        6 doses de polpa enviadas em frio, sem compromisso.
                                    </span>
                                </span>
                            </label>
                            <button
                                type="submit"
                                disabled={sending}
                                data-testid="partner-submit"
                                className="mt-6 w-full inline-flex items-center justify-center gap-2 rounded-full bg-brand-green hover:bg-brand-green-dark text-white px-6 py-4 text-sm font-semibold transition-all shadow-pop disabled:opacity-60"
                            >
                                {sending ? (
                                    <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                    <Send className="h-4 w-4" />
                                )}
                                {sending ? "A enviar..." : "Enviar pedido de proposta"}
                            </button>
                            <p className="mt-3 text-center text-[11px] text-brand-ink-soft">
                                Ao enviar, concordas em ser contactado pela equipa comercial da
                                Sabor Líquido.
                            </p>
                        </>
                    )}
                </form>
            </div>
        </section>
    );
}

const Field = ({ label, children, testid }) => (
    <div data-testid={testid}>
        <label className="text-xs font-bold uppercase tracking-widest text-brand-ink-soft mb-1.5 block">
            {label}
        </label>
        {children}
    </div>
);

const inputCls =
    "w-full rounded-2xl border-2 border-brand-green/15 bg-white px-4 py-3 text-sm text-brand-ink placeholder:text-brand-ink/40 focus:outline-none focus:border-brand-green";
